import { FC, Fragment, PropsWithChildren } from 'react';
import {
  colorDraftBg,
  colorDraftBorder,
  colorInactiveBg,
  colorInactiveBorder,
  colorSuccessBg,
  colorSuccessBorder,
  colorVersion,
  colorVersionBg,
  dividerBorderColor,
} from '@atlas-frontend/design-tokens';
import styled from 'styled-components';
import { Divider, Flex, Tag } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './VersionDisplay.translations.json';

const { addTranslations, t } = i18nNsFactory('version-display');
addTranslations(translations);

export type VersionStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';
export type DataVersionStatus =
  | 'DRAFT'
  | 'CONFIRMED'
  | 'CONF_PUB'
  | 'CONF_PREPUB'
  | 'CONF_WAIT';

export interface VersionDisplayProps extends PropsWithChildren {
  versionNumber: number | null;
  minorNumber?: number | null;
  status: VersionStatus | DataVersionStatus;
}

export const VersionDisplay: FC<VersionDisplayProps> = (props) => {
  const { versionNumber, minorNumber, status, children } = props;
  return (
    <VersionTag className={status}>
      <Flex gap="small" align="center">
        <VersionSpan>
          {`${versionNumber}${minorNumber ? '.' + minorNumber : ''}`}
        </VersionSpan>
        {children}
      </Flex>
    </VersionTag>
  );
};

export const DataVersionStatus = ({ status }: { status: DataVersionStatus }) =>
  t(status)
    .toLocaleUpperCase()
    .split('|')
    .map((status, index) => (
      <Fragment key={index}>
        {index > 0 && <StyledDivider />}
        {status}
      </Fragment>
    ));

export const VersionTag = styled(Tag)`
  margin: 0;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  height: 24px;
  line-height: 1;

  &:is(.DRAFT) {
    border-color: ${colorDraftBorder};
    background-color: ${colorDraftBg};
  }
  &:is(.ACTIVE),
  &:is(.CONFIRMED),
  &:is(.CONF_PUB),
  &:is(.CONF_WAIT),
  &:is(.CONF_PREPUB) {
    border-color: ${colorSuccessBorder};
    background-color: ${colorSuccessBg};
  }
  &:is(.INACTIVE) {
    border-color: ${colorInactiveBorder};
    background-color: ${colorInactiveBg};
  }
`;

const VersionSpan = styled.span`
  background-color: ${colorVersionBg};
  color: ${colorVersion};
  border-radius: 4px;
  padding: 0 3px;
  line-height: 14px;
`;

const StyledDivider = styled(Divider)`
  width: 0;
  min-width: 0;
  border-inline-start: 1px solid ${dividerBorderColor};
  margin: 0 !important;
  min-height: 100%;
  align-self: stretch;
`;
